import { getRequestAsync, postRequestAsync, putRequestAsync, deleteRequestAsync, API_BASE_URL } from '../helpers/apiHandler';

/**
 * WhatsApp Snippets Service
 * Handles all API operations for WhatsApp snippets
 */
class SnippetsService {
  /**
   * Get all snippets for the current user
   * @param {Object} params - Query parameters
   * @param {string} params.accessToken - Authorization token
   * @param {string} params.wabaId - WhatsApp Business Account ID
   * @param {number} params.limit - Number of snippets to fetch
   * @param {number} params.offset - Offset for pagination
   * @param {string} params.search - Search term
   * @param {string} params.sortBy - Sort field
   * @param {string} params.sortOrder - Sort order (ASC/DESC)
   * @returns {Promise<Object>} API response with snippets data
   */
  async getSnippets({
    accessToken,
    wabaId,
    limit = 50,
    offset = 0,
    search = '',
    sortBy = 'updated_at',
    sortOrder = 'DESC'
  }) {
    try {
      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });

      if (wabaId) {
        queryParams.append('waba_id', wabaId);
      }

      if (search) {
        queryParams.append('search', search);
      }

      const response = await getRequestAsync(
        `/v2/whatsapp/manager/snippets?${queryParams.toString()}`,
        {
          headers: { Authorization: accessToken }
        }
      );

      return response;
    } catch (error) {
      console.error('Error fetching snippets:', error);
      throw error;
    }
  }

  /**
   * Get a single snippet by ID
   * @param {number} snippetId - Snippet ID
   * @param {string} accessToken - Authorization token
   * @returns {Promise<Object>} API response with snippet data
   */
  async getSnippet(snippetId, accessToken) {
    try {
      const response = await getRequestAsync(
        `/v2/whatsapp/manager/snippets/${snippetId}`,
        {
          headers: { Authorization: accessToken }
        }
      );

      return response;
    } catch (error) {
      console.error('Error fetching snippet:', error);
      throw error;
    }
  }

  /**
   * Create a new snippet
   * @param {Object} snippetData - Snippet data
   * @param {string} snippetData.name - Snippet name
   * @param {string} snippetData.content - Snippet content
   * @param {Object} snippetData.variables - Optional variables object
   * @param {string} wabaId - WhatsApp Business Account ID
   * @param {string} accessToken - Authorization token
   * @returns {Promise<Object>} API response with created snippet
   */
  async createSnippet(snippetData, wabaId, accessToken) {
    try {
      const payload = {
        name: snippetData.name,
        content: snippetData.content,
        waba_id: wabaId,
        variables: snippetData.variables || {}
      };

      const response = await postRequestAsync(
        '/v2/whatsapp/manager/snippets',
        payload,
        accessToken
      );

      return response;
    } catch (error) {
      console.error('Error creating snippet:', error);
      throw error;
    }
  }

  /**
   * Update an existing snippet
   * @param {number} snippetId - Snippet ID
   * @param {Object} snippetData - Updated snippet data
   * @param {string} snippetData.name - Snippet name
   * @param {string} snippetData.content - Snippet content
   * @param {Object} snippetData.variables - Optional variables object
   * @param {string} accessToken - Authorization token
   * @returns {Promise<Object>} API response with updated snippet
   */
  async updateSnippet(snippetId, snippetData, accessToken) {
    try {
      const payload = {
        name: snippetData.name,
        content: snippetData.content,
        variables: snippetData.variables || {}
      };

      const response = await putRequestAsync(
        `/v2/whatsapp/manager/snippets/${snippetId}`,
        payload,
        accessToken
      );

      return response;
    } catch (error) {
      console.error('Error updating snippet:', error);
      throw error;
    }
  }

  /**
   * Delete a snippet
   * @param {number} snippetId - Snippet ID
   * @param {string} accessToken - Authorization token
   * @returns {Promise<Object>} API response
   */
  async deleteSnippet(snippetId, accessToken) {
    try {
      // Use a custom delete request since deleteRequestAsync doesn't support headers properly
      const response = await fetch(`${API_BASE_URL}/v2/whatsapp/manager/snippets/${snippetId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': accessToken,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete snippet');
      }

      return {
        success: !data.error,
        message: data.message,
        data: data.data || {}
      };
    } catch (error) {
      console.error('Error deleting snippet:', error);
      throw error;
    }
  }

  /**
   * Get agent snippets (for agents/users)
   * @param {Object} params - Query parameters
   * @param {string} params.accessToken - Authorization token
   * @param {string} params.wabaId - WhatsApp Business Account ID
   * @param {number} params.limit - Number of snippets to fetch
   * @param {number} params.offset - Offset for pagination
   * @param {string} params.search - Search term
   * @returns {Promise<Object>} API response with snippets data
   */
  async getAgentSnippets({
    accessToken,
    wabaId,
    limit = 50,
    offset = 0,
    search = ''
  }) {
    try {
      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });

      if (wabaId) {
        queryParams.append('waba_id', wabaId);
      }

      if (search) {
        queryParams.append('search', search);
      }

      const response = await getRequestAsync(
        `/v2/whatsapp/agent/snippets?${queryParams.toString()}`,
        {
          headers: { Authorization: accessToken }
        }
      );

      return response;
    } catch (error) {
      console.error('Error fetching agent snippets:', error);
      throw error;
    }
  }

  /**
   * Transform API snippet data to frontend format
   * @param {Object} apiSnippet - Snippet data from API
   * @returns {Object} Transformed snippet data
   */
  transformSnippetFromApi(apiSnippet) {
    return {
      id: apiSnippet.id,
      name: apiSnippet.name,
      body: apiSnippet.content, // Map content to body for frontend compatibility
      content: apiSnippet.content,
      variables: apiSnippet.variables || {},
      lastModified: apiSnippet.updated_at || apiSnippet.created_at,
      createdBy: apiSnippet.created_by_name || 'Unknown',
      wabaId: apiSnippet.waba_id
    };
  }

  /**
   * Transform frontend snippet data to API format
   * @param {Object} frontendSnippet - Snippet data from frontend
   * @returns {Object} Transformed snippet data for API
   */
  transformSnippetToApi(frontendSnippet) {
    return {
      name: frontendSnippet.name,
      content: frontendSnippet.body || frontendSnippet.content,
      variables: frontendSnippet.variables || {}
    };
  }
}

// Export singleton instance
export default new SnippetsService();